import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API_URL = process.env.REACT_APP_API_URL;
  const token = searchParams.get('token');
  const isActivation = searchParams.get('activation') === 'true';

  const labels = isActivation
    ? {
        title: 'Hesabınızı Aktifleştirin',
        subtitle: 'Devam etmek için hesabınıza ait şifreyi oluşturun.',
        submit: 'Hesabımı Aktif Et',
        submitLoading: 'Aktifleştiriliyor...',
        successTitle: 'Hesabınız Aktif',
        successText: 'Şifreniz oluşturuldu. Birkaç saniye içinde giriş sayfasına yönlendirileceksiniz.',
        successCta: 'Hemen Giriş Yap',
        successToast: 'Hesabınız başarıyla aktifleştirildi',
        errorTitle: 'Geçersiz Aktivasyon Bağlantısı',
        errorText: 'Aktivasyon bağlantısı eksik veya süresi dolmuş. Lütfen yöneticinizle iletişime geçin.',
        invalidLinkToast: 'Geçersiz veya eksik aktivasyon bağlantısı',
      }
    : {
        title: 'Yeni Şifre Belirle',
        subtitle: 'Lütfen yeni şifrenizi girin ve onaylayın.',
        submit: 'Şifreyi Güncelle',
        submitLoading: 'Güncelleniyor...',
        successTitle: 'Şifreniz Güncellendi',
        successText: 'Yeni şifrenizle giriş yapabilirsiniz. Birkaç saniye içinde giriş sayfasına yönlendirileceksiniz.',
        successCta: 'Hemen Giriş Yap',
        successToast: 'Şifreniz başarıyla güncellendi',
        errorTitle: 'Geçersiz Bağlantı',
        errorText: 'Şifre sıfırlama bağlantısı eksik veya hatalı. Lütfen e-postadaki bağlantıyı tekrar kontrol edin.',
        invalidLinkToast: 'Geçersiz veya eksik sıfırlama bağlantısı',
      };

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(labels.invalidLinkToast);
    }
  }, [token, labels.invalidLinkToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success(labels.successToast);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isActivation
        ? 'Hesap aktifleştirilemedi. Bağlantınız geçersiz veya süresi dolmuş olabilir.'
        : 'Şifre güncellenemedi. Bağlantınız geçersiz veya süresi dolmuş olabilir.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9F8F6] to-[#EBE9E4] p-4">
        <Card className="w-full max-w-md" data-testid="reset-password-error-card">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="text-2xl font-bold text-[#1A1A2E]" data-testid="reset-password-error-title">{labels.errorTitle}</h1>
            <p className="text-sm text-gray-600">
              {labels.errorText}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#2563EB] hover:bg-[#1f3829] text-white"
              data-testid="reset-password-back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Giriş Sayfasına Dön
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9F8F6] to-[#EBE9E4] p-4">
        <Card className="w-full max-w-md" data-testid="reset-password-success-card">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
            <h1 className="text-2xl font-bold text-[#1A1A2E]" data-testid="reset-password-success-title">{labels.successTitle}</h1>
            <p className="text-sm text-gray-600">
              {labels.successText}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#2563EB] hover:bg-[#1f3829] text-white"
              data-testid="reset-password-go-to-login-btn"
            >
              {labels.successCta}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9F8F6] to-[#EBE9E4] p-4">
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardContent className="p-8 space-y-5">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-full bg-[#2563EB]/10 flex items-center justify-center mx-auto">
              <KeyRound className="w-7 h-7 text-[#2563EB]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]" data-testid="reset-password-title">{labels.title}</h1>
            <p className="text-sm text-gray-600">
              {labels.subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Yeni Şifre</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="En az 6 karakter"
                required
                minLength={6}
                data-testid="reset-password-new-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Şifre Tekrar</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Şifrenizi tekrar girin"
                required
                minLength={6}
                data-testid="reset-password-confirm-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2563EB] hover:bg-[#1f3829] text-white"
              data-testid="reset-password-submit-btn"
            >
              {loading ? labels.submitLoading : labels.submit}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/login')}
              className="w-full text-gray-600"
              data-testid="reset-password-cancel-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Vazgeç
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
